/**
 * /feoclinicas-2 — versão CLARA da LP do FEO para Clínicas.
 *
 * Mesma estrutura, copys, assets e captura de lead da /feoclinicas (versão
 * escura). Muda apenas a pele visual: fundo claro (off-white), texto navy,
 * teal fechado como acento principal e azul só no Plano Guiado.
 * Tokens `fcl-*` em src/styles.css.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Check,
  Users,
  ClipboardList,
  ArrowRight,
  Search,
  CalendarClock,
  TrendingUp,
  GraduationCap,
  PlaySquare,
  UserRound,
  Gem,
  User,
  CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { B2bLeadDialog } from "@/components/b2b/B2bLeadDialog";
import logoFeo from "@/assets/feo/logo-feo.png";
import graficoCompetencias from "@/assets/b2b/feoclinicas-dashboard-v2.jpg.asset.json";
import planoDeAcao from "@/assets/b2b/feoclinicas-mobile-v2.jpg.asset.json";
import homemApontando from "@/assets/b2b/feoclinicas-apontando.webp.asset.json";
import depo01 from "@/assets/b2b/depo-01.jpg.asset.json";
import depo02 from "@/assets/b2b/depo-02.jpg.asset.json";
import depo03 from "@/assets/b2b/depo-03.jpg.asset.json";
import depo04 from "@/assets/b2b/depo-04.jpg.asset.json";
import depo05 from "@/assets/b2b/depo-05.jpg.asset.json";
import depo06 from "@/assets/b2b/depo-06.jpg.asset.json";
import { B2B_PLANOS, B2B_NUMEROS, B2B_FAQ, type PlanoLead } from "@/lib/b2b/config";
import { trackButtonClick } from "@/lib/tracking";

const TITLE = "Fisio em Ortopedia para Clínicas";
const URL = "https://fisioemortopedia.com.br/feoclinicas-2";
const DESC =
  "Capacitação continuada para equipes de fisioterapia musculoesquelética: diagnóstico da clínica, planos de estudo e mais de 120 cursos.";

export const Route = createFileRoute("/feoclinicas-2")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: B2bLightPage,
});

/** Logo oficial (texto branco) num chip navy — legível sobre fundo claro. */
function LogoChip({ className = "" }: { className?: string }) {
  return (
    <div className={`flex justify-center ${className}`}>
      <span className="inline-flex items-center rounded-2xl bg-fcl-ink px-4 py-2.5 shadow-[0_10px_30px_-18px_rgba(11,18,32,0.6)]">
        <img decoding="async" src={logoFeo} alt="Fisio em Ortopedia" className="h-7 w-auto sm:h-9" />
      </span>
    </div>
  );
}

function B2bLightPage() {
  const [openPlano, setOpenPlano] = useState<PlanoLead | null>(null);

  /** Abre o formulário e registra o clique no CTA para o painel /admin/feoclinicas. */
  function openLead(plano: PlanoLead, label: string) {
    setOpenPlano(plano);
    void trackButtonClick({
      buttonId: `b2b-light-cta-${plano.toLowerCase()}`,
      buttonLabel: label,
      path: "/feoclinicas-2",
    });
  }

  return (
    <div className="min-h-screen bg-fcl-bg text-fcl-ink">
      {/* HERO — véus claros de teal e azul + mockups */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(58% 58% at 6% 8%, rgba(63,213,201,0.22) 0%, transparent 66%)," +
              "radial-gradient(52% 52% at 72% 4%, rgba(37,99,235,0.14) 0%, transparent 70%)," +
              "radial-gradient(60% 50% at 50% 0%, rgba(255,255,255,0.9) 0%, transparent 72%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl px-4 pt-10 sm:px-6 sm:pt-16">
          <div className="mx-auto max-w-5xl text-center">
            <LogoChip className="mb-8" />
            <p className="inline-flex items-center gap-2 rounded-full border border-fcl-teal/25 bg-fcl-teal/[0.08] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-fcl-teal sm:text-[11px]">
              PROGRAMA FEO PARA CLÍNICAS
            </p>
            <h1 className="mt-6 text-balance text-[clamp(1.5rem,6vw,3.4rem)] font-black leading-[1.05] tracking-tight">
              <span className="block whitespace-nowrap">Chega de treinamentos soltos.</span>
              <span className="block whitespace-nowrap">
                Leve toda a{" "}
                <span className="bg-gradient-to-r from-fcl-teal to-fcl-blue bg-clip-text text-transparent">
                  estrutura da FEO
                </span>
              </span>
              <span className="block whitespace-nowrap">
                para dentro da <span className="text-fcl-teal">sua clínica.</span>
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-balance text-base leading-relaxed text-fcl-muted sm:text-lg">
              Toda a sua equipe com acesso a pós-graduações, cursos e discussões clínicas.
              <br className="hidden sm:block" /> Escolha entre duas modalidades: acesso livre às
              formações ou acompanhamento personalizado para cada fisioterapeuta.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                className="rounded-full bg-fcl-teal px-7 font-bold text-white shadow-[0_14px_44px_-16px_rgba(15,156,146,0.55)] transition-all hover:bg-fcl-teal/90 hover:shadow-[0_18px_54px_-16px_rgba(15,156,146,0.7)]"
                onClick={() => openLead("Geral", "Hero, Quero levar a FEO para minha clínica")}
              >
                Quero levar a FEO para minha clínica <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-fcl-line bg-fcl-surface px-7 text-fcl-ink transition-colors hover:border-fcl-teal/40 hover:bg-fcl-band hover:text-fcl-ink"
                asChild
              >
                <a href="#planos">Conhecer as modalidades</a>
              </Button>
            </div>
          </div>

          {/* Mockups: painel na moldura desktop + plano de ação no celular */}
          <div className="relative mt-12 sm:mt-16">
            <div className="mx-auto max-w-4xl overflow-hidden rounded-t-[22px] border border-fcl-line border-b-0 bg-fcl-surface shadow-[0_-10px_90px_-40px_rgba(15,156,146,0.35),0_40px_100px_-50px_rgba(11,18,32,0.35)] sm:rounded-t-[28px]">
              <div className="flex items-center gap-3 border-b border-fcl-line bg-fcl-band px-4 py-3 sm:px-6">
                <span className="inline-flex items-center rounded-lg bg-fcl-ink px-2.5 py-1.5">
                  <img loading="lazy" decoding="async" src={logoFeo} alt="" aria-hidden="true" className="h-4 w-auto sm:h-5" />
                </span>
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-fcl-ink/15" />
                  <span className="h-2 w-2 rounded-full bg-fcl-ink/15" />
                  <span className="h-2 w-2 rounded-full bg-fcl-teal/60" />
                </span>
              </div>
              <div className="overflow-hidden bg-white">
                <img loading="lazy" decoding="async"
                  src={graficoCompetencias.url}
                  alt="Painel da clínica com visão geral da equipe, horas de estudo e formações em andamento"
                  className="block w-full"
                />
              </div>
            </div>

            <div className="absolute bottom-0 right-0 w-[38%] max-w-[230px] sm:right-2 md:right-6">
              <div className="overflow-hidden rounded-t-[22px] border-[5px] border-b-0 border-fcl-ink/85 bg-fcl-ink shadow-[0_28px_60px_-28px_rgba(11,18,32,0.55)] sm:rounded-t-[28px] sm:border-[7px] sm:border-b-0">
                <div className="relative bg-white">
                  <span className="absolute left-1/2 top-1.5 z-10 h-1.5 w-10 -translate-x-1/2 rounded-full bg-black/20" />
                  <img loading="lazy" decoding="async"
                    src={planoDeAcao.url}
                    alt="App do fisioterapeuta com o plano individual, próximas discussões clínicas e cursos em andamento"
                    className="block aspect-[9/17] w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NÚMEROS */}
      <section className="relative border-t border-fcl-line bg-fcl-surface">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-14">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4 sm:divide-x sm:divide-fcl-line">
            {B2B_NUMEROS.map((n) => (
              <div key={n.label} className="text-center sm:px-4">
                <dt className="text-3xl font-black tracking-tight text-fcl-teal sm:text-4xl">
                  {n.valor}
                </dt>
                <dd className="mx-auto mt-2 max-w-[14rem] text-[10px] font-bold uppercase leading-relaxed tracking-[0.16em] text-fcl-muted sm:text-[11px]">
                  {n.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* DORES DO GESTOR */}
      <section className="border-t border-fcl-line bg-fcl-bg">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-center text-[11px] font-black uppercase tracking-[0.24em] text-fcl-blue sm:text-xs">
            Essa frase já passou pela cabeça da maioria dos gestores:
          </p>
          <h2 className="mx-auto mt-4 max-w-4xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            “Eu sei o que deveria estar fazendo. Mas sempre tem alguma coisa mais urgente para
            resolver.”
          </h2>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              {
                icon: Users,
                t: "A clínica ainda depende demais de mim",
                d: "Casos difíceis, dúvidas e decisões técnicas continuam voltando para você.",
              },
              {
                icon: Search,
                t: "Eu não sei o nível técnico real da minha equipe",
                d: "Currículo e experiência não mostram, sozinhos, como cada profissional decide na prática.",
              },
              {
                icon: CalendarClock,
                t: "Não consigo sustentar o desenvolvimento da equipe",
                d: "Treinar, discutir casos e acompanhar evolução sempre disputa espaço com a operação.",
              },
              {
                icon: TrendingUp,
                t: "Quero crescer sem perder o controle da qualidade",
                d: "Mais pacientes e profissionais exigem um padrão técnico que consiga crescer junto.",
              },
            ].map((c) => (
              <div
                key={c.t}
                className="flex gap-4 rounded-2xl border border-fcl-line bg-fcl-surface p-5 shadow-[0_18px_40px_-32px_rgba(11,18,32,0.35)] transition-all duration-300 hover:border-fcl-teal/30 hover:shadow-[0_24px_50px_-30px_rgba(11,18,32,0.35)] sm:p-6"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-fcl-teal/20 bg-fcl-teal/[0.08]">
                  <c.icon className="h-6 w-6 text-fcl-teal" />
                </span>
                <div>
                  <h3 className="text-base font-bold leading-snug sm:text-lg">{c.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fcl-muted">{c.d}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mx-auto mt-12 max-w-5xl text-center text-sm font-bold sm:text-base">
            Quanto mais a clínica cresce,{" "}
            <span className="text-fcl-blue">mais difícil fica garantir o que está sendo entregue</span>{" "}
            por cada profissional.
          </p>
        </div>
      </section>

      {/* POSICIONAMENTO */}
      <section className="border-t border-fcl-line bg-fcl-surface">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-6 px-4 py-16 sm:px-6 sm:py-20 md:grid-cols-2 md:gap-10">
          <div className="relative aspect-[4/5] md:aspect-auto md:h-full md:self-stretch">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(55% 55% at 60% 45%, rgba(63,213,201,0.20) 0%, transparent 72%)",
              }}
            />
            <img
              src={homemApontando.url}
              alt="Fisioterapeuta apontando para a estrutura de formação da FEO para clínicas"
              loading="lazy"
              width={1024}
              height={1024}
              className="absolute inset-y-0 right-0 block h-full w-auto max-w-full rounded-3xl border border-fcl-line object-contain"
            />
          </div>

          <div className="flex min-h-0 flex-col justify-center">
            <h2 className="text-2xl font-black leading-tight tracking-tight sm:text-4xl">
              O FEO para Clínicas existe para você não precisar escolher entre crescer e manter a{" "}
              <span className="text-fcl-teal">qualidade.</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-fcl-muted sm:text-base">
              Em vez de depender de treinamentos pontuais ou do tempo que sobra na agenda do gestor,
              sua clínica passa a contar com uma estrutura contínua de formação para toda a equipe.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-fcl-muted sm:text-base">
              Você escolhe o nível de suporte que faz sentido:{" "}
              <strong className="font-bold text-fcl-teal">acesso livre</strong> às formações da FEO
              ou <strong className="font-bold text-fcl-teal">acompanhamento personalizado</strong>{" "}
              para identificar lacunas, direcionar o desenvolvimento e acompanhar a evolução de cada
              fisioterapeuta.
            </p>
          </div>
        </div>
      </section>

      {/* ECOSSISTEMA */}
      <section className="border-t border-fcl-line bg-fcl-bg">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-fcl-teal/20 bg-fcl-teal/[0.08] px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.24em] text-fcl-teal">
              Acesso completo ao ecossistema FEO
            </span>
          </div>

          <h2 className="mx-auto mt-5 max-w-3xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Tudo o que sua equipe precisa para evoluir,{" "}
            <span className="text-fcl-teal">em um único ambiente.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-fcl-muted sm:text-base">
            Formações baseadas em evidências que já fazem parte da rotina de{" "}
            <strong className="font-bold text-fcl-teal">mais de 45 mil</strong> fisioterapeutas,
            agora disponíveis para toda a sua equipe.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: GraduationCap,
                t: "6 pós-graduações",
                d: "Formações avançadas para aprofundar o raciocínio clínico e fortalecer o padrão técnico da equipe.",
              },
              {
                icon: PlaySquare,
                t: "+120 cursos",
                d: "Conteúdos práticos e atualizados para apoiar a tomada de decisão no dia a dia da clínica.",
              },
              {
                icon: Users,
                t: "Discussões Clínicas Ao Vivo",
                d: "Encontros mensais para discutir casos, atualizar condutas e aproximar ciência da prática clínica.",
              },
            ].map((c) => (
              <div
                key={c.t}
                className="rounded-2xl border border-fcl-line bg-fcl-surface p-6 text-center shadow-[0_18px_40px_-32px_rgba(11,18,32,0.35)] transition-all duration-300 hover:border-fcl-teal/30 hover:shadow-[0_24px_50px_-30px_rgba(11,18,32,0.35)]"
              >
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-fcl-teal/20 bg-fcl-teal/[0.08]">
                  <c.icon className="h-7 w-7 text-fcl-teal" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fcl-muted">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 mb-10 rounded-3xl border border-fcl-blue/25 bg-fcl-blue/[0.05] p-6 shadow-[0_24px_60px_-45px_rgba(37,99,235,0.5)] sm:p-8">
            <p className="mx-auto max-w-3xl text-center text-base font-black leading-snug sm:text-xl">
              E se você quiser um acompanhamento mais próximo,{" "}
              <span className="text-fcl-blue">a FEO</span> também pode direcionar o desenvolvimento
              de cada profissional.
            </p>

            <div className="mt-7 grid gap-4 md:grid-cols-3">
              {[
                {
                  icon: ClipboardList,
                  t: "Diagnóstico da clínica",
                  d: "Um olhar inicial sobre a realidade da equipe para identificar pontos fortes, lacunas e prioridades de desenvolvimento.",
                },
                {
                  icon: UserRound,
                  t: "Avaliação e planejamento individual",
                  d: "Cada fisioterapeuta é avaliado para que o desenvolvimento seja direcionado às necessidades reais de cada profissional.",
                },
                {
                  icon: TrendingUp,
                  t: "Reavaliações",
                  d: "Acompanhamento periódico da evolução da equipe para entender o que avançou e o que ainda precisa ser fortalecido.",
                },
              ].map((c) => (
                <div
                  key={c.t}
                  className="rounded-2xl border border-fcl-blue/15 bg-fcl-surface p-5 transition-colors duration-300 hover:border-fcl-blue/35"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-fcl-blue/25 bg-fcl-blue/[0.08]">
                      <c.icon className="h-5 w-5 text-fcl-blue" />
                    </span>
                    <h3 className="text-sm font-bold leading-snug sm:text-base">{c.t}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-fcl-muted">{c.d}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-8 text-center text-sm font-bold sm:text-base">
            Sua equipe ganha estrutura.{" "}
            <span className="text-fcl-blue">Sua clínica decide o quanto quer acompanhar</span> isso
            de perto.
          </p>
        </div>
      </section>

      {/* PROVA REAL */}
      <section className="border-t border-fcl-line bg-fcl-surface">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-center text-[11px] font-black uppercase tracking-[0.24em] text-fcl-teal sm:text-xs">
            DEPOIMENTOS
          </p>
          <h2 className="mx-auto mt-4 max-w-5xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Mais de 200 clínicas já colocaram o desenvolvimento da equipe nas mãos da FEO.
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm leading-relaxed text-fcl-muted sm:text-base">
            Veja o que muda na rotina de quem decidiu estruturar o desenvolvimento da equipe.
          </p>

          <div className="mt-10 grid gap-8 md:grid-cols-2 md:gap-x-10">
            {[
              {
                n: "01",
                img: depo01.url,
                d: "O desenvolvimento da equipe trouxe mais clareza, organização e propósito para o nosso dia a dia. Hoje todos sabem o que fazer e por quê.",
              },
              {
                n: "02",
                img: depo02.url,
                d: "A rotina ficou mais leve e as entregas mais consistentes. A equipe se comunica melhor e os resultados aparecem toda semana.",
              },
              {
                n: "03",
                img: depo06.url,
                d: "A cultura da clínica mudou. Hoje temos um time engajado, que aprende e cresce junto todos os dias.",
              },
              {
                n: "04",
                img: depo04.url,
                d: "Mais confiança, alinhamento e padrão no atendimento. Isso impactou diretamente na experiência dos nossos pacientes.",
              },
              {
                n: "05",
                img: depo05.url,
                d: "As reuniões ficaram mais produtivas e objetivas. Agora temos dados, acompanhamento e um plano claro de desenvolvimento.",
              },
              {
                n: "06",
                img: depo03.url,
                d: "Depois que estruturamos o desenvolvimento, autonomia e responsabilidade aumentaram muito. A equipe se tornou protagonista.",
              },
            ].map((t) => (
              <figure key={t.n}>
                <div className="rounded-2xl bg-[#ece5dd] p-3 shadow-[0_18px_40px_-30px_rgba(11,18,32,0.45)]">
                  <div className="flex items-start gap-3">
                    <img
                      src={t.img}
                      alt={`Gestor de clínica que participa do programa FEO para Clínicas, depoimento ${t.n}`}
                      loading="lazy"
                      width={512}
                      height={512}
                      className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white sm:h-16 sm:w-16"
                    />
                    <blockquote className="relative flex-1 rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 shadow-sm">
                      <p className="text-[13px] leading-relaxed text-[#1f2b25] sm:text-sm">{t.d}</p>
                    </blockquote>
                  </div>
                </div>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PARA QUEM É */}
      <section className="border-t border-fcl-line bg-fcl-bg">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="relative overflow-hidden rounded-3xl border border-fcl-teal/25 bg-fcl-surface p-8 shadow-[0_30px_70px_-45px_rgba(15,156,146,0.45)] sm:p-12">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 15% 12%, rgba(63,213,201,0.16) 0%, transparent 45%), radial-gradient(circle at 88% 88%, rgba(37,99,235,0.10) 0%, transparent 48%)",
              }}
            />

            <div className="relative">
              <h2 className="mx-auto max-w-2xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
                Se a sua clínica vive isso, o{" "}
                <span className="text-fcl-teal">FEO para Clínicas</span> foi feito para você:
              </h2>

              <ul className="mx-auto mt-8 flex max-w-xl flex-col items-center space-y-4">
                {[
                  "A equipe cresce, mas tudo ainda volta para você.",
                  "Nem todos da equipe te dão a mesma segurança.",
                  "Você investe em treinamentos, mas não vê mudanças no time.",
                  "Falta clareza sobre onde cada profissional precisa evoluir.",
                  "O desenvolvimento só acontece quando sobra tempo.",
                ].map((item) => (
                  <li key={item} className="flex w-full items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-fcl-teal/30 bg-fcl-teal/[0.10]">
                      <Check className="h-3.5 w-3.5 text-fcl-teal" />
                    </span>
                    <span className="text-sm font-medium leading-relaxed text-fcl-muted sm:text-base">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="border-t border-fcl-line bg-fcl-surface">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Escolha o plano ideal para a sua clínica
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-fcl-muted sm:text-base">
            Duas formas de desenvolver sua equipe com a metodologia da FEO.
          </p>

          <div className="mt-10 grid items-stretch gap-5 md:grid-cols-2">
            {B2B_PLANOS.map((p) => {
              const Icon = p.destaque ? Gem : User;
              const accent = p.destaque ? "#2563eb" : "#0f9c92";
              return (
                <div
                  key={p.id}
                  className={`relative flex flex-col rounded-3xl border p-6 transition-all duration-300 sm:p-8 ${
                    p.destaque
                      ? "border-fcl-blue/35 bg-fcl-blue/[0.04] shadow-[0_30px_70px_-50px_rgba(37,99,235,0.6)]"
                      : "border-fcl-line bg-fcl-bg hover:border-fcl-teal/30"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className="grid h-14 w-14 shrink-0 place-items-center rounded-full border"
                      style={{ borderColor: `${accent}44`, background: `${accent}12` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: accent }} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-xl font-black sm:text-2xl">
                        Plano <span style={{ color: accent }}>{p.id}</span>
                      </h3>
                      <p className="mt-1.5 text-sm italic leading-relaxed text-fcl-muted">
                        {p.ideal}
                      </p>
                    </div>
                  </div>

                  <div className="my-6 h-px bg-fcl-line" />

                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-fcl-muted">
                    {p.listaLabel}
                  </p>

                  <ul className="mt-4 space-y-2.5">
                    {p.itens.map((i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-fcl-ink/85">
                        <CheckCircle2
                          className="mt-0.5 h-4 w-4 shrink-0"
                          style={{ color: accent }}
                        />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>

                  <div aria-hidden className="min-h-10 grow" />
                  <div className="pt-0">
                    <Button
                      size="lg"
                      className={`w-full rounded-full font-bold text-white transition-all ${
                        p.destaque
                          ? "bg-fcl-blue shadow-[0_14px_40px_-18px_rgba(37,99,235,0.7)] hover:bg-fcl-blue-deep"
                          : "bg-fcl-teal shadow-[0_14px_40px_-18px_rgba(15,156,146,0.6)] hover:bg-fcl-teal/90"
                      }`}
                      onClick={() => openLead(p.id, `${p.nome}, ${p.cta}`)}
                    >
                      {p.cta}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-fcl-line bg-fcl-bg">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-center text-[11px] font-black uppercase tracking-[0.24em] text-fcl-teal sm:text-xs">
            PERGUNTAS FREQUENTES
          </p>
          <h2 className="mt-4 text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Dúvidas sobre o <span className="text-fcl-teal">FEO para Clínicas</span>
          </h2>

          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {B2B_FAQ.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="overflow-hidden rounded-2xl border border-fcl-line bg-fcl-surface px-5 transition-colors duration-300 hover:border-fcl-teal/30"
              >
                <AccordionTrigger className="py-5 text-left text-sm font-bold leading-snug hover:no-underline sm:text-base">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-fcl-muted whitespace-pre-line">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden border-t border-fcl-line bg-fcl-surface">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 70% at 50% 110%, rgba(63,213,201,0.20) 0%, transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Vamos desenhar o programa da sua clínica
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-fcl-muted sm:text-base">
            Preencha seus dados e nossa equipe entra em contato pelo WhatsApp para agendar uma
            reunião online e apresentar o programa em detalhes.
          </p>
          <Button
            size="lg"
            className="mt-8 rounded-full bg-fcl-teal px-8 font-bold text-white shadow-[0_16px_48px_-18px_rgba(15,156,146,0.6)] transition-all hover:bg-fcl-teal/90 hover:shadow-[0_20px_58px_-18px_rgba(15,156,146,0.75)]"
            onClick={() => openLead("Geral", "CTA final, Tenho interesse")}
          >
            Tenho interesse <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </section>

      <MinimalFooter variant="light" />

      <B2bLeadDialog plano={openPlano} onClose={() => setOpenPlano(null)} />
    </div>
  );
}
